import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import stakingAbi from "./StakingV4ABI.json";

//const STAKING_ADDRESS = "0x804f92390a98307aE748D7fe5477242532eb3FD3";
//const TOKEN_ADDRESS = "0xd02d468C3032Cae2F80959B35dE3a73c7A7614a4";
//const CHAIN_ID = "0xaa36a7"; // Sepolia

const STAKING_ADDRESS = "0xC3A14dcc88D77Eb65C74aDE295185267FbB94930";
const TOKEN_ADDRESS = "0xD3caeCab1663ccF26ced842cFa73BB1bB3A05029";
const CHAIN_ID = "0x1";

const erc20Abi = [
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)"
];

function App() {
  const [account, setAccount] = useState(null);
  const [stakingContract, setStakingContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);

  const [stakeAmount, setStakeAmount] = useState("");
  const [lockDays, setLockDays] = useState("");
  const [unstakeAmount, setUnstakeAmount] = useState("");

  const [tokenBalance, setTokenBalance] = useState("0");
  const [allowance, setAllowance] = useState("0");
  const [myStake, setMyStake] = useState("0");

  const [boardData, setBoardData] = useState([]);
  const [stakeError, setStakeError] = useState("");
  const [unstakeError, setUnstakeError] = useState("");
  const [loadingStake, setLoadingStake] = useState(false);
  const [loadingUnstake, setLoadingUnstake] = useState(false);

  useEffect(() => {
    document.title = "DDI Staking Board";
  }, []);

  const connectWallet = async () => {
    try {
      if (!window.ethereum) throw new Error("MetaMask not installed");

      await window.ethereum.request({ method: "eth_requestAccounts" });
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: CHAIN_ID }]
      });

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      const stakingCtr = new ethers.Contract(STAKING_ADDRESS, stakingAbi, signer);
      const tokenCtr = new ethers.Contract(TOKEN_ADDRESS, erc20Abi, signer);

      setAccount(address);
      setStakingContract(stakingCtr);
      setTokenContract(tokenCtr);

      await loadData(stakingCtr, tokenCtr, address);
    } catch (err) {
      setStakeError(err.reason || err.message);
    }
  };

  const loadData = async (stakingCtr, tokenCtr, user) => {
    const balanceRaw = await tokenCtr.balanceOf(user);
    const allowanceRaw = await tokenCtr.allowance(user, STAKING_ADDRESS);
    const myStakeRaw = await stakingCtr.stakeOf(user);

    setTokenBalance(ethers.formatUnits(balanceRaw, 18));
    setAllowance(ethers.formatUnits(allowanceRaw, 18));
    setMyStake(ethers.formatUnits(myStakeRaw, 18));

    const boardAddresses = await stakingCtr.getBoard();

    const enrichedBoard = await Promise.all(
      boardAddresses.map(async (addr) => {
        if (addr === ethers.ZeroAddress) {
          return { address: "-", stake: "-", lockEnd: "-", color: "#f87171" };
        }

        const stakeRaw = await stakingCtr.stakeOf(addr);
        const lockRaw = await stakingCtr.lockEnd(addr);
        const now = Math.floor(Date.now() / 1000);
        const fourYears = 4 * 365 * 24 * 3600;

        let color = "#f87171"; // red by default
        if (Number(lockRaw) - now >= fourYears) {
          color = "#22c55e"; // green if more than 4 years
        }

        return {
          address: addr,
          stake: ethers.formatUnits(stakeRaw, 18),
          lockEnd:
            Number(lockRaw) === 0
              ? "-"
              : new Date(Number(lockRaw) * 1000).toLocaleString(),
          color
        };
      })
    );

    setBoardData(enrichedBoard);
  };

  const handleStake = async () => {
    try {
      setLoadingStake(true);
      setStakeError("");

      const parsedAmount = ethers.parseUnits(stakeAmount, 18);
      const currentAllowance = await tokenContract.allowance(account, STAKING_ADDRESS);

      if (currentAllowance < parsedAmount) {
        const approveTx = await tokenContract.approve(STAKING_ADDRESS, parsedAmount);
        await approveTx.wait();
      }

      const stakeTx = await stakingContract.stake(parsedAmount, Number(lockDays));
      await stakeTx.wait();

      await loadData(stakingContract, tokenContract, account);
      setStakeAmount("");
      setLockDays("");
    } catch (err) {
      setStakeError(err.reason || err.message);
    } finally {
      setLoadingStake(false);
    }
  };

  const handleUnstake = async () => {
    try {
      setLoadingUnstake(true);
      setUnstakeError("");

      const parsed = ethers.parseUnits(unstakeAmount, 18);
      const tx = await stakingContract.unstake(parsed);
      await tx.wait();

      await loadData(stakingContract, tokenContract, account);
      setUnstakeAmount("");
    } catch (err) {
      setUnstakeError(err.reason || err.message);
    } finally {
      setLoadingUnstake(false);
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>DDI Staking Board</h1>

      {!account && (
        <button style={styles.primaryButton} onClick={connectWallet}>
          Connect Wallet
        </button>
      )}

      {account && (
        <>
          <div style={styles.card}>
            <div style={styles.wallet}>
              Connected Wallet:
              <div style={styles.walletAddress}>{account}</div>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statBox}>
                <div style={styles.statLabel}>Token Balance</div>
                <div style={styles.statValue}>{tokenBalance}</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statLabel}>Allowance</div>
                <div style={styles.statValue}>{allowance}</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statLabel}>Your Stake</div>
                <div style={styles.statValue}>{myStake}</div>
              </div>
            </div>
          </div>

          <div style={styles.actionsRow}>
            <div style={styles.card}>
              <h3>Stake</h3>
              <input
                style={styles.input}
                placeholder="Amount"
                value={stakeAmount}
                onChange={(e) => setStakeAmount(e.target.value)}
              />
              <input
                style={styles.input}
                placeholder="Lock Days"
                value={lockDays}
                onChange={(e) => setLockDays(e.target.value)}
              />
              <button
                style={styles.primaryButton}
                onClick={handleStake}
                disabled={loadingStake}
              >
                {loadingStake ? "Processing..." : "Approve + Stake"}
              </button>
              {stakeError && <div style={styles.error}>{stakeError}</div>}
            </div>

            <div style={styles.card}>
              <h3>Unstake</h3>
              <input
                style={styles.input}
                placeholder="Amount"
                value={unstakeAmount}
                onChange={(e) => setUnstakeAmount(e.target.value)}
              />
              <button
                style={styles.secondaryButton}
                onClick={handleUnstake}
                disabled={loadingUnstake}
              >
                {loadingUnstake ? "Processing..." : "Unstake"}
              </button>
              {unstakeError && <div style={styles.error}>{unstakeError}</div>}
            </div>
          </div>

          <div style={styles.card}>
            <h2>Board Members</h2>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Address</th>
                  <th>Staked</th>
                  <th>Lock Expiration</th>
                </tr>
              </thead>
              <tbody>
                {boardData.map((member, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td style={styles.wrapCell}>{member.address}</td>
                    <td>{member.stake}</td>
                    <td style={{ color: member.color }}>{member.lockEnd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", padding: "60px", background: "linear-gradient(135deg,#0f172a,#1e293b)", color: "#f1f5f9", fontFamily: "system-ui, sans-serif" },
  title: { fontSize: "2.4rem", marginBottom: 40 },
  card: { background: "rgba(255,255,255,0.06)", backdropFilter: "blur(12px)", padding: 28, borderRadius: 18, marginBottom: 30, boxShadow: "0 12px 32px rgba(0,0,0,0.35)" },
  wallet: { marginBottom: 25, fontSize: 14, opacity: 0.8 },
  walletAddress: { marginTop: 6, fontSize: 13, wordBreak: "break-all" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 20 },
  statBox: { background: "rgba(255,255,255,0.05)", padding: 18, borderRadius: 14, overflow: "hidden" },
  statLabel: { fontSize: 13, opacity: 0.65 },
  statValue: { marginTop: 8, fontSize: 18, fontWeight: 600, wordBreak: "break-all" },
  actionsRow: { display: "flex", gap: 30, flexWrap: "wrap" },
  input: { width: "100%", padding: 10, marginBottom: 12, borderRadius: 8, border: "none" },
  primaryButton: { padding: "10px 18px", background: "#6366f1", border: "none", borderRadius: 8, color: "white", cursor: "pointer" },
  secondaryButton: { padding: "10px 18px", background: "#14b8a6", border: "none", borderRadius: 8, color: "white", cursor: "pointer" },
  table: { width: "100%", borderCollapse: "collapse", marginTop: 20 },
  wrapCell: { wordBreak: "break-all" },
  error: { color: "#f87171", marginTop: 10, fontSize: 13 }
};

export default App;